/**
 * kPluralCore - pick the FORM of a counted phrase that the language's own grammar asks for.
 *
 * WHY IT EXISTS. A counted phrase written as one template - "{0} days ago" - is wrong for a count
 * of one in every language this estate ships ("1 days ago"), and wrong for most counts in Arabic,
 * which has a singular, a dual, a form for 3-10, a form for 11-99 and a form for round hundreds.
 * The rule that decides which form a number takes is NOT something to write per product: it is
 * CLDR's plural categories, and every runtime this estate targets carries them as
 * `Intl.PluralRules`. This class is the one place that asks.
 *
 * WHAT A CALLER SUPPLIES. The forms it has, by category, and the count. `Other` is the only
 * required form and is the answer for every category the caller left out or left empty - so a
 * language that needs two forms supplies two, Arabic supplies five or six, and a caller that
 * supplies only `Other` behaves exactly as an un-pluralised phrase did.
 *
 *     const Text: string = kPluralCore.Pick(Days, {
 *         Other: LNG.DAYSAGO, One: LNG.DAYSAGO_ONE, Two: LNG.DAYSAGO_TWO,
 *         Few: LNG.DAYSAGO_FEW, Many: LNG.DAYSAGO_MANY,
 *     }, kPluralCore.LocaleFor(ks.UserLanguageName));
 *
 * IT RETURNS THE FORM, NOT THE SENTENCE. Substituting the number is the caller's, because how a
 * number is written (digits, separators, a word) is a separate decision this class has no view on -
 * an Arabic dual, for one, is normally written with no number in it at all.
 *
 * WHAT A SECOND CONSUMER WOULD CHANGE: nothing here. The forms and the locale are arguments. A
 * language the LNG system gains later needs one line in `_LocaleByLanguage`; until it has one,
 * `LocaleFor` answers English rules, which is the un-pluralised behaviour plus a correct singular.
 *
 * ⚠ `LocaleFor` EXISTS BECAUSE AN UNKNOWN LOCALE DOES NOT FAIL - IT ANSWERS THE HOST'S DEFAULT.
 * `new Intl.PluralRules("xx")` resolves a well-formed tag it has no data for to the RUNTIME's own
 * default locale, so the same code would pluralise by the server's language on one machine and the
 * browser's on another. The LNG system names its languages in English words ("Swedish"), which are
 * not locale tags at all; mapping them here, with a stated answer for an unknown one, is what makes
 * the category a function of the language the phrases are in and of nothing else.
 *
 * Server-safe: no DOM, no window, no LNG. Safe in Node.js, Azure Functions and the browser.
 */

import { getCoreUtils } from "./kCoreHandler";

/** The forms of one counted phrase, by CLDR plural category. Only `Other` is required. */
export interface IkPluralForms {
    Other: string;
    Zero?: string;
    One?: string;
    Two?: string;
    Few?: string;
    Many?: string;
}

export class kPluralCore {

    /** The CLDR category names, as `Intl.PluralRules.select` answers them. */
    public static readonly Zero: string = "zero";
    public static readonly One: string = "one";
    public static readonly Two: string = "two";
    public static readonly Few: string = "few";
    public static readonly Many: string = "many";
    public static readonly Other: string = "other";

    /** The locale whose rules apply where the language is not known. */
    public static readonly DefaultLocale: string = "en";

    /** LNG language name -> the locale tag whose plural rules its phrases follow. The names are the
     *  ones `LNG.SetLanguage` takes; English is the default language and has no `SetLanguage` case,
     *  and is listed so that it is an answer rather than a fallback. */
    private static _LocaleByLanguage: { [Name: string]: string } = {
        Arabic: "ar", Danish: "da", Dutch: "nl", English: "en", French: "fr", German: "de",
        Italian: "it", Norwegian: "nb", Portuguese: "pt", Spanish: "es", Swedish: "sv",
    };

    /** One rule set per locale, built on first use: constructing one costs far more than asking it. */
    private static _Rules: { [Locale: string]: Intl.PluralRules } = {};

    /** The locale tag for an LNG language name. An unknown name answers `DefaultLocale`. */
    public static LocaleFor = (LanguageName: string): string => {
        if (getCoreUtils().KErrorMode) { return kPluralCore.DefaultLocale; }
        try {
            const Name: string = String(LanguageName).trim();
            if (Object.prototype.hasOwnProperty.call(kPluralCore._LocaleByLanguage, Name) === false) {
                return kPluralCore.DefaultLocale;
            }
            return kPluralCore._LocaleByLanguage[Name];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPluralCore.LocaleFor");
            return kPluralCore.DefaultLocale;
        }
    };

    /**
     * The plural category a count takes in a locale: zero, one, two, few, many or other.
     *
     * The sign is dropped first - "3 days ago" and "-3" are the same grammatical number - and a
     * runtime with no `Intl.PluralRules` answers the two-form rule (one for exactly 1, other for
     * everything else), which is right for nine of the eleven languages this estate ships and is
     * the honest floor for the rest.
     */
    public static Category = (Count: number, Locale: string = kPluralCore.DefaultLocale): string => {
        if (getCoreUtils().KErrorMode) { return kPluralCore.Other; }
        try {
            const Size: number = Math.abs(Number(Count));
            if (typeof Intl === "undefined" || typeof Intl.PluralRules !== "function") {
                if (Size === 1) { return kPluralCore.One; }
                return kPluralCore.Other;
            }
            if (Object.prototype.hasOwnProperty.call(kPluralCore._Rules, Locale) === false) {
                kPluralCore._Rules[Locale] = new Intl.PluralRules(Locale);
            }
            return kPluralCore._Rules[Locale].select(Size);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPluralCore.Category");
            return kPluralCore.Other;
        }
    };

    /**
     * The form of a counted phrase for this count, in this locale.
     *
     * A category the caller supplied no form for - or an empty one - answers `Other`, so a caller
     * states only the forms its languages distinguish.
     */
    public static Pick = (Count: number, Forms: IkPluralForms,
                          Locale: string = kPluralCore.DefaultLocale): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Form: string | undefined = kPluralCore._FormFor(Forms,
                kPluralCore.Category(Count, Locale));
            if (Form === undefined || Form === null || Form === "") { return Forms.Other; }
            return Form;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPluralCore.Pick");
            return "";
        }
    };

    /** The caller's form for one category, or undefined where it gave none. */
    private static _FormFor = (Forms: IkPluralForms, Category: string): string | undefined => {
        if (getCoreUtils().KErrorMode) { return undefined; }
        try {
            if (Category === kPluralCore.One) { return Forms.One; }
            if (Category === kPluralCore.Two) { return Forms.Two; }
            if (Category === kPluralCore.Few) { return Forms.Few; }
            if (Category === kPluralCore.Many) { return Forms.Many; }
            if (Category === kPluralCore.Zero) { return Forms.Zero; }
            return Forms.Other;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPluralCore._FormFor");
            return undefined;
        }
    };
}
